import type { SocketClientAck, SocketClientOptions, SocketLike } from './types';

export class SocketClient {
  private socket: SocketLike | null = null;
  private readonly listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  constructor(private readonly options: SocketClientOptions) {}

  connect(): SocketLike {
    if (this.socket) {
      return this.socket;
    }

    const token = this.options.getAccessToken();
    if (!token) {
      throw new Error('SocketClient: access token is missing');
    }

    this.socket = this.options.ioFactory(this.options.apiOrigin, {
      path: this.options.path,
      auth: { token },
      withCredentials: this.options.withCredentials ?? false,
    });
    this.listeners.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        this.socket?.on(event, handler);
      });
    });

    return this.socket;
  }

  disconnect(): void {
    if (!this.socket) {
      return;
    }
    this.socket.disconnect();
    this.socket = null;
  }

  reconnectWithFreshToken(): SocketLike {
    this.disconnect();
    return this.connect();
  }

  isConnected(): boolean {
    if (!this.socket) {
      return false;
    }
    return (this.socket as { connected?: boolean }).connected === true;
  }

  on(event: string, handler: (...args: unknown[]) => void): () => void {
    const handlers = this.listeners.get(event) ?? new Set<(...args: unknown[]) => void>();
    handlers.add(handler);
    this.listeners.set(event, handlers);
    this.socket?.on(event, handler);

    return () => {
      this.listeners.get(event)?.delete(handler);
      this.socket?.off(event, handler);
    };
  }

  onConnect(handler: () => void): () => void {
    return this.on('connect', handler);
  }

  onDisconnect(handler: (reason: string) => void): () => void {
    return this.on('disconnect', handler as (...args: unknown[]) => void);
  }

  emitWithAck(event: string, payload?: unknown): Promise<void> {
    const socket = this.requireSocket();
    return new Promise<void>((resolve, reject) => {
      socket.emit(event, payload, (ack: SocketClientAck) => {
        if (ack?.ok) {
          resolve();
          return;
        }
        reject(new Error(ack?.error || `Socket ack failed for event: ${event}`));
      });
    });
  }

  private requireSocket(): SocketLike {
    if (!this.socket) {
      throw new Error('SocketClient: call connect() first');
    }
    return this.socket;
  }
}
